/**
 * FuncionPremiumModal — Modal oficial GLOBAL "Funcion Premium".
 *
 * Activacion:
 *   window.dispatchEvent(new CustomEvent('damyxia:funcion-premium'))
 *
 * Uso:
 *   Se muestra al usuario Free cuando intenta usar cualquier funcion avanzada:
 *   Motor Pro, Variantes Pro, Fondos, Generacion Normal
 *   desde el Historial, usar imagen del Historial como base, etc.
 *
 * Estructura (NO MODIFICAR textos oficiales):
 *   1. Titulo: "Funcion Premium"
 *   2. Subtitulo: "Esta funcion no esta disponible en tu plan actual."
 *   3. Texto explicativo oficial.
 *   4. Lista de beneficios Premium (7 items).
 *   5. CTA principal: "Actualizar al Plan Premium" -> scroll suave al bloque
 *      [data-testid="premium-plan-section"]. Si no esta en /planes-y-beneficios,
 *      navega alli primero y dispara el scroll al montar.
 *   6. CTA secundario: "Seguir explorando" -> cierra el modal.
 *
 * Politica: bloquea la accion original. No ejecuta ninguna funcion avanzada.
 * No abre nuevas paginas ni recarga.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import VoiceInfoBlock from './blocks/VoiceInfoBlock';
import { navigateToPremium } from '../utils/navigateToPremium';

const textsES = {
  title: 'Funcion Premium',
  subtitle: 'Esta funcion no esta disponible en tu plan actual.',
  body:
    'Para acceder a esta herramienta necesitas el Plan Premium. Tu plan actual (Free) solo permite ver, descargar, marcar como favoritas y eliminar imagenes.',
  benefits_label: 'Beneficios Premium',
  benefits: [
    'Motor Pro',
    'Variantes Pro',
    'Fondos',
    'Usar imagenes del Historial como base',
    'Acceso completo al Historial Avanzado',
    'Todas las funciones avanzadas sin limites',
  ],
  cta_primary: 'Actualizar al Plan Premium',
  cta_secondary: 'Seguir explorando',
};

// Texto oficial para la variante "voz" (microfono bloqueado para Free)
const voiceES = {
  title: 'Habla con DamyxIA — exclusivo en Premium',
  subtitle: 'La conversacion por voz esta reservada al Plan Premium.',
  body:
    'Accede a comunicacion verbal fluida con DamyxIA: habla naturalmente, ve la transcripcion en tiempo real y recibe respuestas habladas y escritas. Una experiencia humana, moderna y premium.',
  benefits_label: 'Que incluye',
  benefits: [
    'Reconocimiento de voz en tiempo real',
    'Transcripcion automatica al chat',
    'Conversaciones naturales y fluidas',
    'Idiomas: Espanol e Ingles',
    'Sin limites de uso',
  ],
  cta_primary: 'Actualizar al Plan Premium',
  cta_secondary: 'Seguir explorando',
};

const voiceEN = {
  title: 'Talk to DamyxIA — Premium only',
  subtitle: 'Voice conversation is reserved for the Premium Plan.',
  body:
    'Access fluid verbal communication with DamyxIA: speak naturally, see live transcription and receive spoken and written responses. A human, modern and premium experience.',
  benefits_label: "What's included",
  benefits: [
    'Real-time speech recognition',
    'Automatic transcription to chat',
    'Natural and fluid conversations',
    'Languages: Spanish and English',
    'No usage limits',
  ],
  cta_primary: 'Upgrade to Premium',
  cta_secondary: 'Keep exploring',
};

const textsEN = {
  title: 'Premium Feature',
  subtitle: 'This feature is not available on your current plan.',
  body:
    'To access this tool you need the Premium Plan. Your current plan (Free) only allows you to view, download, favorite and delete images.',
  benefits_label: 'Premium benefits',
  benefits: [
    'Pro Engine',
    'Pro Variants',
    'Backgrounds',
    'Use History images as a base',
    'Full access to Advanced History',
    'All advanced features without limits',
  ],
  cta_primary: 'Upgrade to Premium',
  cta_secondary: 'Keep exploring',
};

const FuncionPremiumModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState('es');
  const [variant, setVariant] = useState('default'); // 'default' | 'voice'

  useEffect(() => {
    const open = (e) => {
      // Permite override de idioma via event.detail.language
      if (e?.detail?.language) setLanguage(e.detail.language);
      // Permite override de variant via event.detail.variant
      const v = e?.detail?.variant;
      setVariant(v === 'voice' ? 'voice' : 'default');
      setIsOpen(true);
    };
    window.addEventListener('damyxia:funcion-premium', open);
    return () => window.removeEventListener('damyxia:funcion-premium', open);
  }, []);

  // Detectar idioma del usuario (localStorage) cuando se monta
  useEffect(() => {
    try {
      const lang = localStorage.getItem('damyxia_language');
      if (lang === 'en' || lang === 'es') setLanguage(lang);
    } catch { /* ignore */ }
  }, []);

  const close = () => setIsOpen(false);

  const handleUpgrade = () => {
    close();
    navigateToPremium();
  };

  if (!isOpen) return null;

  const t = variant === 'voice'
    ? (language === 'en' ? voiceEN : voiceES)
    : (language === 'en' ? textsEN : textsES);

  return createPortal(
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center p-4"
      data-testid="funcion-premium-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="funcion-premium-title"
    >
      <div className="fixed inset-0 bg-black/75 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-zinc-900 border border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-amber-500/15 via-zinc-900 to-zinc-900 border-b border-amber-500/20">
          <h2 id="funcion-premium-title" className="text-xl font-bold text-white" data-testid="funcion-premium-title">
            {t.title}
          </h2>
          <p className="text-sm text-amber-200/90 mt-1" data-testid="funcion-premium-subtitle">
            {t.subtitle}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {variant === 'voice' && (
            <div data-testid="voice-info-block-premium-wrapper">
              <VoiceInfoBlock hideWhenActive={false} languageOverride={language} />
            </div>
          )}
          <p className="text-sm text-slate-300 leading-relaxed" data-testid="funcion-premium-body">
            {t.body}
          </p>

          <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4">
            <p className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-2">
              {t.benefits_label}
            </p>
            <ul className="space-y-1.5 text-sm text-slate-200" data-testid="funcion-premium-benefits">
              {t.benefits.map((b) => (
                <li key={b} className="flex gap-2">
                  <span className="text-amber-400">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            onClick={handleUpgrade}
            className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold rounded-xl transition-colors text-sm"
            data-testid="funcion-premium-cta-primary"
          >
            {t.cta_primary}
          </button>

          <button
            type="button"
            onClick={close}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-200 transition-colors bg-transparent"
            data-testid="funcion-premium-cta-secondary"
          >
            {t.cta_secondary}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FuncionPremiumModal;
